import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker as MuiDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import TextField from '@mui/material/TextField';
import ArrowBack from '@mui/icons-material/ArrowBackIos';
import ArrowForward from '@mui/icons-material/ArrowForwardIos';
import { useTheme } from '@mui/material/styles';

import {
  DATE_DISPLAY_FORMAT_NO_COMMA,
  DATE_DISPLAY_FORMAT_NO_COMMA_WITH_YEAR
} from 'catering/constants/dates';
import {
  getSectionNavColor,
  getSectionNavMediumColor,
  getSectionNavLightColor,
  getSectionNavLighterColor,
  getPageBackgroundColor,
  getButtonFontHoverColor,
  getButtonBackgroundHoverColor,
  getMenuItemNamesColor,
  getCardBackgroundColor,
  getItemDescriptionsAndPricesTypography
} from 'styles/helpers/getThemeStyles';

function getTransitionStyles(theme) {
  let styles = {};
  const { fontFamily, fontWeight } = getItemDescriptionsAndPricesTypography(
    theme
  );
  styles = {
    '& .PrivatePickersFadeTransitionGroup-root': {
      color: getSectionNavColor(theme),
      fontFamily,
      fontWeight
    },
    '& .MuiTypography-caption': {
      color: getSectionNavMediumColor(theme)
    },
    '& .MuiPickersDay-root': {
      color: getMenuItemNamesColor(theme),
      backgroundColor: getCardBackgroundColor(theme)
    },
    '& .MuiPickersDay-root:hover': {
      color: getButtonFontHoverColor(theme),
      backgroundColor: getButtonBackgroundHoverColor(theme)
    },
    '& .MuiPickersDay-root.Mui-selected': {
      color: getButtonFontHoverColor(theme),
      // important is needed here as date pickers built in styles override this on render
      backgroundColor: `${getButtonBackgroundHoverColor(theme)} !important`
    },
    '& .MuiPickersDay-root.Mui-disabled': {
      color: getSectionNavLightColor(theme)
    }
  };
  return styles;
}

function getPaperStyles(theme) {
  const styles = {
    backgroundColor: getPageBackgroundColor(theme)
  };
  return styles;
}

function getCustomArrows(theme) {
  let arrows = {};
  const arrowStyles = {
    fill: getSectionNavLightColor(theme),
    fontSize: '1rem'
  };
  arrows = {
    // eslint-disable-next-line react/display-name
    LeftArrowIcon: () => <ArrowBack sx={arrowStyles} />,
    // eslint-disable-next-line react/display-name
    RightArrowIcon: () => <ArrowForward sx={arrowStyles} />
  };
  return arrows;
}

const StyledTextField = styled(TextField)(({ theme }) => {
  let labelStyles = {};
  let inputStyles = {};
  labelStyles = {
    color: getSectionNavColor(theme),
    ...getItemDescriptionsAndPricesTypography(theme)
  };
  inputStyles = {
    ...getItemDescriptionsAndPricesTypography(theme)
  };
  return {
    '& .MuiOutlinedInput-input': {
      border: `1px solid ${getSectionNavLighterColor(theme)}`,
      borderRadius: 4,
      color: getSectionNavMediumColor(theme),
      padding: '25px 12px 10px',
      ...inputStyles,
      '&:hover': {
        cursor: 'pointer'
      }
    },
    '& .MuiInputLabel-root': labelStyles
  };
});

const NEXT_MONTH_LABEL = 'next month';
const PREVIOUS_MONTH_LABEL = 'previous month';

function DatePicker({ InputProps = {}, value, ...rest }) {
  const thisYear = new Date().getFullYear();
  const format =
    value.getFullYear() !== thisYear
      ? DATE_DISPLAY_FORMAT_NO_COMMA_WITH_YEAR
      : DATE_DISPLAY_FORMAT_NO_COMMA;

  const theme = useTheme();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        id="date-picker"
        allowSameDateSelection
        closeOnSelect
        showToolbar={false}
        inputFormat={format}
        componentsProps={{
          leftArrowButton: {
            'data-testid': 'date-picker-left-arrow',
            'aria-label': PREVIOUS_MONTH_LABEL
          },
          rightArrowButton: {
            'data-testid': 'date-picker-right-arrow',
            'aria-label': NEXT_MONTH_LABEL
          },
          actionBar: {
            actions: []
          }
        }}
        value={value}
        renderInput={params => (
          <StyledTextField
            {...params}
            variant="outlined"
            data-testid="date-picker"
          />
        )}
        views={['day']}
        DialogProps={{
          TransitionProps: {
            sx: getTransitionStyles(theme)
          },
          PaperProps: {
            sx: getPaperStyles(theme)
          }
        }}
        components={getCustomArrows(theme)}
        {...rest}
      />
    </LocalizationProvider>
  );
}

DatePicker.propTypes = {
  InputProps: PropTypes.object,
  value: PropTypes.object.isRequired
};

export default DatePicker;
